import { useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney } from "../engine/dates";
import { clearPaydownOverride, setPaydownOverride } from "../lib/data";

/**
 * The payments the limit forces, and whether they can actually be made.
 *
 * The forecast can work out what the card needs. It cannot know what the bank
 * can stand, and a paydown the business cannot fund is not a plan — it is a
 * number that makes the forecast look solvent while the card gets declined in
 * real life.
 *
 * So each one can be reduced or refused. Refusing does not tidy the problem
 * away: the card stays over its limit and says so, because that is the actual
 * consequence.
 */
export default function Paydowns({ paydowns = [], weeks = [], overdraftLimit, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  if (!paydowns.length) return null;

  // Whether the bank can stand it. The closing balance already contains this
  // paydown, so a week that ends below the overdraft is a week where it could
  // not have been made.
  const affordability = (dateIso) => {
    if (!overdraftLimit) return null;
    const w = weeks.find((x) => {
      const s = x.start.toISOString().slice(0, 10);
      const e = x.end.toISOString().slice(0, 10);
      return dateIso >= s && dateIso <= e;
    });
    if (!w) return null;
    return { endBal: w.endBal, over: w.endBal < -overdraftLimit };
  };

  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const total = paydowns.reduce((s, p) => s + p.amount, 0);
  const refused = paydowns.filter((p) => p.skipped);

  const cell = { padding: "8px 10px", fontSize: 12 };
  const th = { ...cell, textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 };

  return (
    <div style={{ marginTop: 22 }}>
      <h3 style={{ fontFamily: fontDisplay, fontSize: 17, margin: "0 0 4px" }}>
        Payments the limit forces
      </h3>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 760, marginTop: 0 }}>
        What the card would need paying to stay inside {fmtMoney(paydowns[0].limit)}. Reduce one if
        only part of it is possible, or refuse it outright — the card then stays over its limit in
        the forecast, which is what would actually happen.
      </p>

      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 8 }}>{error}</div>}

      <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto", maxWidth: 940 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>When</th>
              <th style={{ ...th, textAlign: "right" }}>Needed</th>
              <th style={{ ...th, textAlign: "right" }}>Paying</th>
              <th style={th}>Can the bank stand it?</th>
              <th style={{ ...th, width: 150 }} />
            </tr>
          </thead>
          <tbody>
            {paydowns.map((p) => {
              const afford = affordability(p.date);
              return (
                <tr
                  key={p.date}
                  style={{
                    borderTop: `1px solid ${T.borderSoft}`,
                    background: p.skipped ? "#FBEFE2" : undefined,
                    opacity: p.skipped ? 0.75 : 1,
                  }}
                >
                  <td style={cell}>
                    {fmtDay(p.date)}
                    <div style={{ fontSize: 10, color: T.muted }}>
                      would reach {fmtMoney(p.wouldReach)}
                    </div>
                  </td>
                  <td style={{ ...cell, textAlign: "right", fontFamily: fontMono, color: T.muted }}>
                    {fmtMoney(p.suggested)}
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    <input
                      key={`a-${p.date}-${p.amount}`}
                      defaultValue={p.skipped ? 0 : p.amount}
                      disabled={busy === p.date}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v === p.amount) return;
                        if (v <= 0) {
                          run(p.date, () => setPaydownOverride(p.date, { skip: true, amount: null }));
                        } else if (Math.abs(v - p.suggested) < 0.01) {
                          run(p.date, () => clearPaydownOverride(p.date));
                        } else {
                          run(p.date, () => setPaydownOverride(p.date, { skip: false, amount: v }));
                        }
                      }}
                      style={{
                        fontFamily: fontMono,
                        fontSize: 12,
                        padding: "4px 6px",
                        width: 96,
                        textAlign: "right",
                        borderRadius: 4,
                        border: `1px solid ${p.overridden || p.skipped ? T.accent : T.border}`,
                        background: p.overridden || p.skipped ? T.accentSoft : "transparent",
                      }}
                    />
                  </td>
                  <td style={{ ...cell, fontSize: 11 }}>
                    {afford == null ? (
                      <span style={{ color: T.muted }}>—</span>
                    ) : afford.over ? (
                      <span style={{ color: T.cost, fontWeight: 600 }}>
                        No · that week ends {fmtMoney(afford.endBal)}, past a{" "}
                        {fmtMoney(-overdraftLimit)} overdraft
                      </span>
                    ) : (
                      <span style={{ color: T.muted }}>
                        Yes · that week ends {fmtMoney(afford.endBal)}
                      </span>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    {p.skipped ? (
                      <button
                        onClick={() => run(p.date, () => clearPaydownOverride(p.date))}
                        disabled={busy === p.date}
                        style={{
                          border: `1px solid ${T.border}`,
                          background: "transparent",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        put it back
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          run(p.date, () => setPaydownOverride(p.date, { skip: true, amount: null }))
                        }
                        disabled={busy === p.date}
                        style={{
                          border: `1px solid ${T.border}`,
                          background: "transparent",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 11,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          color: T.muted,
                        }}
                      >
                        can&rsquo;t do this
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.6, maxWidth: 760 }}>
        {fmtMoney(total)} in total{refused.length ? `, with ${refused.length} refused` : ""}.
        {refused.length > 0 && (
          <>
            {" "}
            <strong style={{ color: T.ink }}>
              Where a paydown is refused the card stays over its limit
            </strong>
            , so spend after that date may be declined — the forecast keeps showing it because
            removing it would hide the problem rather than solve it.
          </>
        )}
      </div>
    </div>
  );
}
